import React from 'react'

import { Button, Modal, ModalInjectedProps } from '@sketch/components'
import {
  GetProjectsDocument,
  GetProjectsListQuery,
  GetProjectsListQueryVariables,
  GetProjectsQueryVariables,
  GetSharesQuery,
  useUnarchiveProjectMutation,
} from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import {
  removeFromPaginated,
  useFlag,
  useFolderTerm,
} from '@sketch/modules-common'
import { useEventDispatch } from '@sketch/utils'
import { evictFieldIf } from '@sketch/graphql-cache'

interface UnarchiveProjectModalProps extends ModalInjectedProps {
  projectIdentifier: string
  workspaceIdentifier: string
  projectName: string
}

const UnarchiveProjectModal = (props: UnarchiveProjectModalProps) => {
  const { hideModal, projectIdentifier, projectName, workspaceIdentifier } =
    props
  const { showToast } = useToast()
  const dispatchSharesRefresh = useEventDispatch('workspaceShareRefresh')
  const isNestedProjectsOn = useFlag('nested-projects')

  const [unarchiveProject, { loading }] = useUnarchiveProjectMutation({
    variables: { projectIdentifier },
    onError: 'show-toast',
    onCompleted: () => {
      showToast(`“${projectName}” unarchived`)
      hideModal()
    },
    update: cache => {
      // Remove the project from the project from the archived listing
      removeFromPaginated(
        cache,
        { __typename: 'Project', identifier: projectIdentifier },
        key => key.includes('projects')
      )

      // Remove from cache every query related with this workspace shares
      // This way the view will be refetched from scratch if needed to make sure
      // we always get the correct server information
      const workspaceCacheId = cache.identify({
        __typename: 'Workspace',
        identifier: workspaceIdentifier,
      })
      const sharesFieldName: keyof GetSharesQuery['workspace'] = 'shares'
      cache.evict({
        id: workspaceCacheId,
        fieldName: sharesFieldName,
      })

      // We also remove the archived listing to make sure it's refreshed
      const projectFieldName: keyof GetProjectsListQuery['workspace'] =
        'projects'
      evictFieldIf<{ archived: GetProjectsListQueryVariables['archived'] }>(
        cache,
        {
          id: workspaceCacheId,
          fieldName: projectFieldName,
        },
        fieldArguments => fieldArguments?.archived === 'ARCHIVED'
      )

      cache.gc()

      // Force any visible query to refresh
      dispatchSharesRefresh({
        workspaceIdentifier,
        projectIdentifier,
      })
    },
    refetchQueries: () => [
      {
        query: GetProjectsDocument,
        variables: {
          workspaceId: workspaceIdentifier,
          topLevel: !isNestedProjectsOn,
        } satisfies GetProjectsQueryVariables,
      },
    ],
  })

  const folderTerm = useFolderTerm()

  return (
    <Modal onCancel={hideModal}>
      <Modal.Header>Unarchive {folderTerm.upper}?</Modal.Header>
      <Modal.Body>
        Unarchiving “{projectName}” will send it back to your {folderTerm.lower}
        s.
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={hideModal}>Cancel</Button>
        <Button
          variant="primary"
          onClick={() => unarchiveProject()}
          loading={loading}
        >
          Unarchive
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default UnarchiveProjectModal
